import identity from '@/api/services/identity';
import { usePermissionsStore } from '@/store/usePermissionsStore';
import { extractApiError } from '@/utils/apiError';

export const useAuth = () => {
  const user = useState('user', () => null);
  const loading = useState('auth-loading', () => true);
  const config = useRuntimeConfig();

  const signup = async ({ username, email, password, signup_code }) => {
    try {
      const response = await identity.register({ username, email, password, signup_code });
      if (response?.tokenType === 'Bearer') {
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      await checkAuth();
      return { success: true };
    } catch (error) {
      console.error('Signup failed:', error);
      // Error from handleError is the response object directly ({ status, data }).

      // Handle 403 for invalid signup code
      if (error?.status === 403) {
        return { success: false, error: 'Invalid signup code' };
      }
      // Flatten validation/error payloads (string, { detail }, [{ msg }], ...)
      // into a clean message so the UI never renders raw JSON.
      return { success: false, error: extractApiError(error, 'Signup failed') };
    }
  };

  const checkAuth = async () => {
    try {
      loading.value = true;
      const token = localStorage.getItem('token');
      if (!token) {
        user.value = null;
        return;
      }
      try {
        const me = await identity.me();
        user.value = me;
        // Load user permissions in background (non-blocking)
        const permissionsStore = usePermissionsStore();
        permissionsStore.loadPermissions();
      } catch (err) {
        if (err?.status === 401) {
          try {
            const refreshed = await identity.refresh();
            if (refreshed?.accessToken) {
              localStorage.setItem('token', refreshed.accessToken);
              localStorage.setItem('refreshToken', refreshed.refreshToken);
              const me = await identity.me();
              user.value = me;
              // Load user permissions in background (non-blocking)
              const permissionsStore = usePermissionsStore();
              permissionsStore.loadPermissions();
            }
          } catch (_err) {
            user.value = null;
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          }
        } else {
          throw err;
        }
      }
    } finally {
      loading.value = false;
    }
  };

  const signin = async ({ email, password }) => {
    try {
      const response = await identity.login({ email, password });
      if (!response) return { success: false, error: response };
      if (response.tokenType === 'Bearer') {
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      await checkAuth();
      return { success: true };
    } catch (error) {
      if (error?.statusCode === 401 && error?.data?.detail) {
        return { success: false, error: error.data.detail };
      }
      return { success: false, error: 'Login failed' };
    }
  };

  const signinWithCode = async ({ email, code }) => {
    try {
      const response = await identity.loginWithCode(email, code);
      if (!response) return { success: false, error: 'Sign in failed' };
      if (response.tokenType === 'Bearer') {
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      // Mark the session as recovery-grade so the dashboard can show the
      // one-time-code banner and the optional set-a-new-password nudge.
      sessionStorage.setItem('otpLogin', '1');
      if (response.resetToken) sessionStorage.setItem('otpResetToken', response.resetToken);
      await checkAuth();
      return { success: true };
    } catch (error) {
      return { success: false, error: extractApiError(error, 'Invalid or expired code') };
    }
  };

  const logout = async () => {
    try {
      await identity.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('otpLogin');
    sessionStorage.removeItem('otpResetToken');
    user.value = null;
    // Clear permissions on logout
    const permissionsStore = usePermissionsStore();
    permissionsStore.clearPermissions();
  };

  return {
    user,
    loading,
    signup,
    checkAuth,
    signin,
    signinWithCode,
    logout,
    hasToken: computed(() => !!localStorage.getItem('token')),
  };
};
